import React, { FC, useCallback, useMemo, useRef } from 'react';
import { v4 } from 'uuid';
import { groupBy, compact, map } from 'lodash-es';
import { Box, Typography } from '@mui/material';
import { FormProvider, useFieldArray } from 'react-hook-form';
import { EventPriceTypesEnum, MembershipDto, waitAsync } from '@sweatpals/shared';
import { CreateEventTierDto, PricesFormDto } from '../../../models/dto';
import { usePricesModalState } from '../hooks/use-prices-modal-state.hook';
import { DonationIcon, EditIcon, PlusIcon, UsersOutlined } from '../../../icons';
import {
	CoreLabeledCheckbox,
	CoreLinkButton,
	CoreNumberInput,
	CoreRadio,
	CoreRadioGroup,
	RadioFormLabel,
} from '../../_core';
import { PromocodesModal } from '../promocodes-modal/promocodes-modal.component';
import { PriceCard } from './price-card.component';
import { Colors, EventTypeEnum } from '../../../constants';
import { Showable } from '../../utils';
import { PromocodeChip } from '../promocodes-modal/promocode-chip.component';
import { RightBlackModal } from '../../right-modal';
import { extendPriceToMembershipListWithMissingMemberships, scrollToFirstErrorElement } from '../../../utils';

export const ENABLE_MINIMAL_DONATION_AMOUNT_SETTING = false;
export const defaultPriceTierName = 'General Admission';

export interface PricesModalProps<TValues = PricesFormDto> {
	open: boolean;
	data?: TValues;
	boughtTicketsByTier?: Record<string, number>;
	promocodesUsesById?: Record<string, number>;
	showPromocodesModal?: boolean;
	communityMemberships?: MembershipDto[] | undefined;
	openPromocodesModal?(): void;
	closePromocodesModal?(): void;
	onClose(isDirty?: boolean): void;
	setConfirmCloseCallback?(callback: () => void): void;
	onSubmit?(data: TValues): void;
}

export const PricesModal: FC<PricesModalProps> = props => {
	const {
		open,
		data: initialData,
		communityMemberships,
		boughtTicketsByTier,
		promocodesUsesById,
		showPromocodesModal,
		onClose,
		onSubmit,
		openPromocodesModal,
		closePromocodesModal,
		setConfirmCloseCallback,
	} = props;

	const formRef = useRef<HTMLDivElement>(null);

	const defaultTier = useMemo((): CreateEventTierDto => ({
		key: v4(),
		priceAmount: undefined as any,
		tierName: defaultPriceTierName,
		tierTypeId: EventPriceTypesEnum.Standard,
		withMaxTickets: false,
		maxTickets: null,
		order: 0,
	}), []);

	const defaultValues = useMemo(() => {
		if (!initialData) {
			return {
				type: EventTypeEnum.Free,
				attendeesLimit: null,
				withAttendeesLimit: false,
				tiers: [defaultTier],
			}
		}

		return {
			...initialData,
			tiers: initialData?.tiers?.map(tier => ({
				...tier,
				eventPricesToMemberships: tier.withMemberships ? extendPriceToMembershipListWithMissingMemberships(
					tier.eventPricesToMemberships,
					communityMemberships
				) : [],
			})) ?? [defaultTier],
		}
	}, [communityMemberships, defaultTier, initialData])

	const { form: pricesForm } = usePricesModalState<PricesFormDto>({
		defaultValues: structuredClone(defaultValues),
		mode: 'onBlur',
	});

	const pricesFormData = pricesForm.watch();
	const pricesFormErrors = pricesForm.formState.errors;

	const handleSave = useCallback(async (data: PricesFormDto) => {
		await pricesForm.trigger();
		const errors = pricesForm.formState.errors;

		if (Object.keys(errors).length) {
			return;
		}

		await waitAsync(500);

		onSubmit?.({
			...data,
			minimalDonationAmount: data.type === EventTypeEnum.Donation ? data.minimalDonationAmount : undefined,
			discounts: data.type === EventTypeEnum.Paid ? data.discounts : undefined,
			tiers:
				data.type === EventTypeEnum.Paid
					? (data.tiers?.map((tier, index) => ({
							...tier,
							order: index,
							formKey: undefined,
					})) as PricesFormDto['tiers'])
					: undefined,
		});
	}, [onSubmit, pricesForm]);

	const {
		fields: priceTiers = [],
		append: addTier,
		remove: removeTiers,
		move: moveTiers
	} = useFieldArray({
		name: `tiers`,
		control: pricesForm.control,
		keyName: 'formKey', // render key
	});

	const promocodes = pricesFormData.discounts;

	const handleClose = useCallback(() => {
		onClose?.(pricesForm.formState.isDirty);
	}, [onClose, pricesForm.formState.isDirty]);

	const tiersError = useMemo(
		() => (pricesForm.formState.errors.tiers?.root ?? pricesForm.formState.errors.tiers),
		[pricesForm.formState.errors.tiers]
	);

	const nonUniqueTiers = useMemo(() => {
		if (tiersError?.type !== 'arrayUnique') {
			return [];
		}

		const groupedTiers = groupBy(pricesFormData.tiers, 'tierName');
		const mappedTiers = map(groupedTiers, (tiers, tierName) => (tiers.length > 1 ? tierName : undefined));

		return compact(mappedTiers);
	}, [pricesFormData.tiers, tiersError]);

	const addNewTier = useCallback(() => {
		addTier({
			key: v4(),
			priceAmount: undefined as any,
			tierTypeId: EventPriceTypesEnum.Standard,
			withMaxTickets: false,
			maxTickets: null,
			tierName: priceTiers.length < 1 ? defaultPriceTierName : '',
			order: priceTiers.length,
		});
	}, [addTier, priceTiers]);

	const validateForm = useCallback(async (): Promise<void> => {
		await pricesForm.trigger();

		scrollToFirstErrorElement(formRef.current);
	}, [pricesForm]);

	return (
		<FormProvider {...pricesForm}>
			<RightBlackModal
				open={open}
				onClose={handleClose}
				headerTitle='Ticket Settings'
				rootProps={{ sx: { px: '12px', maxWidth: { xs: '100%', md: '720px' } } }}
				contentSx={{ px: '12px' }}
				headerSx={{ px: '12px' }}
				modalProps={{
					component: 'form',
					onSubmit: pricesForm.handleSubmit(handleSave, validateForm),
				}}
				submitButtonProps={{
					type: 'submit',
					disabled: !!tiersError,
					loading: pricesForm.formState.isSubmitting,
				}}
				contentBoxRef={formRef}
			>
				<Box>
					<CoreRadioGroup
						row
						sx={{
							'& .MuiFormControlLabel-root': {
								mr: '43px',
							}
						}}
						value={pricesFormData?.type}
						onChange={e => {
							pricesForm.setValue('type', e.target.value as EventTypeEnum);
						}}
					>
						<RadioFormLabel label={'Free'} control={<CoreRadio />} value={EventTypeEnum.Free} />
						<RadioFormLabel label={'Paid'} control={<CoreRadio />} value={EventTypeEnum.Paid} />
						<RadioFormLabel label={'Donation'} control={<CoreRadio />} value={EventTypeEnum.Donation} />
					</CoreRadioGroup>
				</Box>

				<Box display="flex" gap="12px">
					<CoreLabeledCheckbox
						value={pricesFormData.withAttendeesLimit}
						onChange={(_, checked) => {
							pricesForm.setValue('withAttendeesLimit', checked);

							if (!checked) {
								pricesForm.setValue('attendeesLimit', null);
							}
						}}
						label={(
							<Box display="flex" alignItems="center" gap="10px">
								<UsersOutlined />
								<Typography
									sx={{
										fontFamily: 'Montserrat, sans-serif',
										fontSize: '14px',
										fontWeight: 600,
										lineHeight: '17px',
									}}
								>
									Event Capacity
								</Typography>
							</Box>
						)}
						labelSx={{
							'&.Mui-disabled': {
								color: Colors.white,
							},
						}}
						sx={{
							"&:hover": {
								backgroundColor: 'transparent',
							},

							"& .MuiTouchRipple-root": {
								display: 'none',
							}
						}}
					/>
					<Showable show={pricesFormData.withAttendeesLimit}>
						<CoreNumberInput
							value={pricesFormData.attendeesLimit}
							onChange={value => pricesForm.setValue(`attendeesLimit`, value ?? null)}
							decimalScale={0}
							allowNegative={false}
							lang='en-US'
							onBlur={() => {
								if (pricesFormErrors.attendeesLimit) {
									pricesForm.trigger('attendeesLimit');
								}
							}}
							error={!!pricesFormErrors.attendeesLimit}
							helperText={pricesFormErrors.attendeesLimit?.message}
							placeholder='Enter max limit'
							controlSx={{ flex: 1 }}
						/>
					</Showable>
				</Box>

				<Box
					sx={{
						width: '100%',
						borderBottom: `1px solid ${Colors.white_a10}`,
					}}
				/>

				<Showable show={pricesFormData?.type === EventTypeEnum.Paid}>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '16px',
						}}
					>
						{priceTiers?.map((tier, index) => (
							<PriceCard
								id={tier.key}
								key={tier.formKey}
								index={index}
								nonUniqueTiers={nonUniqueTiers}
								onRemove={() => {
									removeTiers(index);
								}}
								onMove={moveTiers}
								canDrag={priceTiers.length > 1}
								communityMemberships={communityMemberships}
								totalTicketsCount={boughtTicketsByTier?.[tier.key]}
							/>
						))}
					</Box>

					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '32px',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '10px',
							}}
						>
							<CoreLinkButton type='button' onClick={addNewTier}>
								<PlusIcon />
								Tier
							</CoreLinkButton>

							<Showable show={pricesFormData?.type === EventTypeEnum.Paid && !promocodes?.length}>
								<CoreLinkButton type='button' onClick={openPromocodesModal}>
									<PlusIcon />
									Promo code
								</CoreLinkButton>
							</Showable>
						</Box>

						<Showable show={promocodes?.length}>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: '8px',
								}}
							>
								<Typography
									sx={{
										fontFamily: 'Montserrat, sans-serif',
										fontSize: '15px',
										fontWeight: '600',
										color: Colors.white,
									}}
								>
									Promo codes
								</Typography>

								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: '40px',
									}}
								>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											flexWrap: 'wrap',
											gap: '4px',
											flexGrow: 1,
										}}
									>
										{promocodes?.map((promocode, index) => (
											<PromocodeChip key={promocode.code} data={promocode} />
										))}
									</Box>

									<Box
										sx={{
											p: '4px',
											cursor: 'pointer',
										}}
										onClick={openPromocodesModal}
									>
										<EditIcon />
									</Box>
								</Box>
							</Box>
						</Showable>
					</Box>
				</Showable>

				<Showable show={pricesFormData?.type === EventTypeEnum.Donation}>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '16px',
						}}
					>
						<CoreNumberInput
							fullWidth
							error={!!pricesFormErrors.minimalDonationAmount}
							helperText={pricesFormErrors.minimalDonationAmount?.message}
							startAdornment={<DonationIcon style={{ width: '20px', height: '20px' }} />}
							prefix='$'
							decimalScale={2}
							allowNegative={false}
							value={
								typeof pricesFormData?.minimalDonationAmount === 'number' &&
								!isNaN(Number(pricesFormData.minimalDonationAmount))
									? pricesFormData?.minimalDonationAmount / 100
									: ''
							}
							onChange={value => {
								const newValue = value ? Math.round(value * 100) : undefined;
								pricesForm.setValue('minimalDonationAmount', newValue);
							}}
							onBlur={() => {
								pricesForm.trigger(`minimalDonationAmount`);
							}}
							name='minimalDonationAmount'
							label='Minimal Donation Amount'
							placeholder='$0'
						/>
					</Box>
				</Showable>
			</RightBlackModal>

			{showPromocodesModal && (
				<PromocodesModal
					open={showPromocodesModal ?? false}
					tiersOptions={
						(pricesFormData.tiers ?? [])
							.filter(t => t.tierName)
							.map(({ key, tierName }) => ({ value: key, label: tierName }))
					}
					data={promocodes?.length ? { discounts: promocodes } : undefined}
					promocodesUsesById={promocodesUsesById}
					onClose={(isDirty) => {
						const closeHandler = () => {
							closePromocodesModal?.();
						};

						if (isDirty) {
							setConfirmCloseCallback?.(() => closeHandler);
						} else {
							closeHandler();
						}
					}}
					onSubmit={data => {
						pricesForm.setValue('discounts', data.discounts);
						closePromocodesModal?.();
					}}
				/>
			)}
		</FormProvider>
	);
};
